import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Users, Plus, UserPlus, Crown, Zap, X, ArrowRight, Trash2, Trophy,
  Target, TrendingUp, Medal, BarChart3,
} from "lucide-react";

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [inviteEmail, setInviteEmail] = useState({});   // { groupId: email }
  const [activeTab, setActiveTab] = useState({});       // { groupId: "members" | "leaderboard" }
  const [boards, setBoards] = useState({});             // { groupId: leaderboardData }
  const [boardsLoading, setBoardsLoading] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/groups/me");
      setGroups(data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    const name = newName.trim();
    if (name.length < 2) { toast.error("Enter a group name (2-40 chars)"); return; }
    setCreating(true);
    try {
      await api.post("/groups", { name });
      setNewName("");
      toast.success("Group created");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create group");
    } finally { setCreating(false); }
  };

  const invite = async (groupId) => {
    const email = (inviteEmail[groupId] || "").trim().toLowerCase();
    if (!email) { toast.error("Enter an email"); return; }
    try {
      await api.post(`/groups/${groupId}/invite`, { email });
      setInviteEmail((s) => ({ ...s, [groupId]: "" }));
      toast.success("Member added");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add member");
    }
  };

  const removeMember = async (groupId, memberId) => {
    if (!window.confirm("Remove this member?")) return;
    try {
      await api.delete(`/groups/${groupId}/members/${memberId}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  };

  const del = async (groupId) => {
    if (!window.confirm("Delete this group?")) return;
    try {
      await api.delete(`/groups/${groupId}`);
      toast.success("Group deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const loadBoard = async (groupId) => {
    setBoardsLoading((s) => ({ ...s, [groupId]: true }));
    try {
      const { data } = await api.get(`/groups/${groupId}/leaderboard`);
      setBoards((s) => ({ ...s, [groupId]: data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load leaderboard");
    } finally {
      setBoardsLoading((s) => ({ ...s, [groupId]: false }));
    }
  };

  const switchTab = (groupId, tab) => {
    setActiveTab((s) => ({ ...s, [groupId]: tab }));
    if (tab === "leaderboard" && !boards[groupId]) loadBoard(groupId);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-2 flex items-center gap-2">
            <Zap className="w-3 h-3" /> Paid members only
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mb-3">
            Your <span className="gold-text-soft">Race Groups</span>
          </h1>
          <p className="text-muted-foreground leading-relaxed max-w-2xl">
            Create a private group of up to <span className="font-black text-brand-gold">10 paid members</span>, invite them by email, and race on the same passage at the same second. Both you and every invitee need an active plan.
          </p>
        </div>

        {/* Create new group */}
        <div className="rounded-2xl border border-brand-gold/25 bg-card/40 p-5 mb-8" data-testid="groups-create">
          <div className="flex items-center gap-2 mb-3">
            <Plus className="w-4 h-4 text-brand-gold" />
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold">Create a group</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Allahabad HC Batch 2026"
              className="bg-background border-brand-gold/30 text-foreground placeholder:text-muted-foreground focus-visible:ring-brand-gold"
              maxLength={40}
              data-testid="group-name-input"
            />
            <Button
              onClick={create}
              disabled={creating}
              className="rounded-xl h-11 px-5 bg-gradient-to-r from-[#E5C158] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#B8860B] text-[#0F172A] text-xs uppercase tracking-widest font-black"
              data-testid="group-create-btn"
            >
              {creating ? "Creating…" : "Create group"} <Plus className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Group list */}
        {loading ? (
          <div className="text-muted-foreground font-mono">Loading groups…</div>
        ) : groups.length === 0 ? (
          <div className="rounded-2xl border border-brand-gold/15 bg-card/30 p-10 text-center" data-testid="groups-empty">
            <Users className="w-10 h-10 mx-auto text-brand-gold/60 mb-3" />
            <div className="font-display text-lg font-black mb-1">No groups yet</div>
            <div className="text-sm text-muted-foreground">Create your first group above to start inviting friends.</div>
          </div>
        ) : (
          <div className="space-y-5" data-testid="groups-list">
            {groups.map((g) => (
              <div key={g.id} className="rounded-2xl border border-brand-gold/25 bg-card/40 p-5" data-testid={`group-${g.id}`}>
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-brand-gold" />
                      <h2 className="font-display text-xl font-black tracking-tight">{g.name}</h2>
                      <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                        {g.member_count} / {g.max_members} members
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">Created {new Date(g.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to="/race">
                      <Button className="rounded-lg h-9 px-3 bg-brand-crimson hover:bg-red-700 text-white text-[10px] uppercase tracking-widest font-black" data-testid={`group-race-${g.id}`}>
                        <Trophy className="w-3.5 h-3.5 mr-1.5" /> Start race
                      </Button>
                    </Link>
                    <Button
                      onClick={() => del(g.id)}
                      variant="outline"
                      className="rounded-lg h-9 px-2 border-brand-crimson/40 hover:bg-brand-crimson/10 text-brand-crimson text-[10px] uppercase tracking-widest font-bold"
                      data-testid={`group-del-${g.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-1 mb-4 border-b border-brand-gold/10 -mx-1 px-1">
                  {[
                    { id: "members", label: "Members", icon: Users },
                    { id: "leaderboard", label: "Leaderboard", icon: BarChart3 },
                  ].map((tab) => {
                    const Icon = tab.icon;
                    const active = (activeTab[g.id] || "members") === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => switchTab(g.id, tab.id)}
                        data-testid={`group-tab-${tab.id}-${g.id}`}
                        className={`flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-widest font-black transition-colors border-b-2 -mb-px ${
                          active
                            ? "border-brand-gold text-brand-gold"
                            : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" /> {tab.label}
                      </button>
                    );
                  })}
                </div>

                {(activeTab[g.id] || "members") === "members" && <>
                {/* Members */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                  {g.members.map((m) => (
                    <div key={m.id} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/40 border border-brand-gold/10">
                      <div className="w-8 h-8 rounded-full bg-brand-gold/15 border border-brand-gold/40 flex items-center justify-center text-brand-gold text-xs font-black">
                        {(m.name || "?").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{m.name} {m.is_owner && <Crown className="w-3 h-3 text-brand-gold inline ml-1" />}</div>
                        <div className="text-[10px] text-muted-foreground truncate">{m.email}</div>
                      </div>
                      {!m.is_owner && (
                        <button
                          onClick={() => removeMember(g.id, m.id)}
                          className="w-6 h-6 rounded hover:bg-brand-crimson/10 text-brand-crimson opacity-60 hover:opacity-100 flex items-center justify-center"
                          title="Remove member"
                          data-testid={`group-remove-${g.id}-${m.id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Invite */}
                {g.member_count < g.max_members && (
                  <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t border-brand-gold/10">
                    <Input
                      value={inviteEmail[g.id] || ""}
                      onChange={(e) => setInviteEmail((s) => ({ ...s, [g.id]: e.target.value }))}
                      placeholder="Invite by email (must be a paid member)"
                      type="email"
                      className="bg-background border-brand-gold/30 text-foreground placeholder:text-muted-foreground focus-visible:ring-brand-gold"
                      data-testid={`group-invite-input-${g.id}`}
                    />
                    <Button
                      onClick={() => invite(g.id)}
                      className="rounded-xl h-11 px-5 bg-brand-gold hover:bg-brand-gold/90 text-[#0F172A] text-xs uppercase tracking-widest font-black"
                      data-testid={`group-invite-btn-${g.id}`}
                    >
                      <UserPlus className="w-4 h-4 mr-2" /> Invite
                    </Button>
                  </div>
                )}
                </>}

                {(activeTab[g.id] || "members") === "leaderboard" && (
                  <div data-testid={`group-leaderboard-${g.id}`}>
                    {boardsLoading[g.id] ? (
                      <div className="text-sm font-mono text-muted-foreground py-6 text-center">Loading leaderboard…</div>
                    ) : boards[g.id]?.rows?.length ? (
                      <div className="overflow-x-auto rounded-xl border border-brand-gold/15">
                        <table className="w-full text-sm">
                          <thead className="bg-secondary/60">
                            <tr>
                              <th className="text-left px-3 py-2.5 text-[9px] uppercase tracking-[0.3em] font-black text-muted-foreground w-12">Rank</th>
                              <th className="text-left px-3 py-2.5 text-[9px] uppercase tracking-[0.3em] font-black text-muted-foreground">Member</th>
                              <th className="text-right px-3 py-2.5 text-[9px] uppercase tracking-[0.3em] font-black text-muted-foreground">Attempts</th>
                              <th className="text-right px-3 py-2.5 text-[9px] uppercase tracking-[0.3em] font-black text-muted-foreground">Avg WPM</th>
                              <th className="text-right px-3 py-2.5 text-[9px] uppercase tracking-[0.3em] font-black text-muted-foreground">Avg Acc</th>
                              <th className="text-right px-3 py-2.5 text-[9px] uppercase tracking-[0.3em] font-black text-muted-foreground">Best WPM</th>
                              <th className="text-right px-3 py-2.5 text-[9px] uppercase tracking-[0.3em] font-black text-muted-foreground">Best Acc</th>
                            </tr>
                          </thead>
                          <tbody>
                            {boards[g.id].rows.map((r) => {
                              const medal = r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : r.rank;
                              return (
                                <tr key={r.user_id} className={`border-t border-brand-gold/10 ${r.is_self ? "bg-brand-emerald/5" : ""}`} data-testid={`board-row-${r.user_id}`}>
                                  <td className="px-3 py-2.5 font-mono font-black">{medal}</td>
                                  <td className="px-3 py-2.5">
                                    <div className="flex items-center gap-2">
                                      <div className="w-7 h-7 rounded-full bg-brand-gold/15 border border-brand-gold/40 flex items-center justify-center text-brand-gold text-[10px] font-black shrink-0">
                                        {(r.name || "?").slice(0, 2).toUpperCase()}
                                      </div>
                                      <div className="min-w-0">
                                        <div className="text-sm font-bold truncate flex items-center gap-1.5">
                                          {r.name}
                                          {r.is_owner && <Crown className="w-3 h-3 text-brand-gold" />}
                                          {r.is_self && <span className="text-[9px] uppercase tracking-widest font-black text-brand-emerald ml-1">You</span>}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono">{r.attempts}</td>
                                  <td className="px-3 py-2.5 text-right font-mono font-black text-brand-gold">{r.avg_wpm}</td>
                                  <td className="px-3 py-2.5 text-right font-mono"><span className={r.avg_accuracy >= 90 ? "text-brand-emerald font-black" : ""}>{r.avg_accuracy}%</span></td>
                                  <td className="px-3 py-2.5 text-right font-mono">{r.best_wpm}</td>
                                  <td className="px-3 py-2.5 text-right font-mono">{r.best_accuracy}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Medal className="w-8 h-8 mx-auto text-brand-gold/60 mb-2" />
                        <div className="text-sm font-mono text-muted-foreground">No attempts yet. Complete some sessions to rank up.</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
